<template>
  <MPage ref="pageRef" showBack>
    <template #headerLeft>
      <BaseTag :text="pageTag.status" :options="pageTag.options" />
    </template>

    <template #headerRight>
      <slot name="headerRight"></slot>
      <HeaderBtns
        :key="pageProps.headerBtns?.length"
        :schema="pageProps.headerBtns"
        :pageMode="pageMode"
        :actionHandler="actionHandler"
      />
    </template>

    <slot name="pageHeader"></slot>

    <FormWithTabs ref="formTabsRef" v-model:pageTag="pageTag" v-bind="bindFormEvents">
      <template v-for="item in Object.keys($slots)" #[item]="data">
        <slot :name="item" v-bind="data || {}"></slot>
      </template>
    </FormWithTabs>

    <slot></slot>

    <BasePageModal
      :getContainer="() => ($refs.pageRef as any)?.wrapRef"
      @register="registerModal"
    />
  </MPage>
</template>

<script setup lang="ts">
  import { computed, nextTick, ref } from 'vue';
  import { useRoute } from 'vue-router';
  import { deepMerge, message } from '@mts-fe/utils';
  import { BaseTag } from '@mts/components/Base';
  import { MPage } from '@mts/components/Page';
  import type {
    ActionHandlerFn,
    FormWithTabsExposed,
    PAGE_ITEM_SCHEMA,
    PageMode,
    SchemaPageItemProps,
  } from '..';
  import BasePageModal from '../components/BasePageModal.vue';
  import FormWithTabs from '../components/FormWithTabs.vue';
  import HeaderBtns from '../components/HeaderBtns.vue';
  import {
    createSchemaPageContext,
    FORM_EMIT_EVENTS,
    getCurrentPageMode,
    parsePageItemSchema,
    useActionHandler,
    useFormEvents,
    usePageModal,
    useSchemaActionContext,
    useSchemaGlobalContext,
    useSchemaRender,
  } from '..';

  defineOptions({
    name: 'SchemaPageItem',
  });

  const props = defineProps<{ schema: PAGE_ITEM_SCHEMA }>();
  const emit = defineEmits(FORM_EMIT_EVENTS);
  const bindFormEvents = useFormEvents(emit);

  const route = useRoute();
  const id = (route.params.id || route.query.id)?.toString();
  const curId = id && !Number.isNaN(id) ? id : void 0;
  const pageMode = getCurrentPageMode() || ((curId ? 'Edit' : 'Add') as PageMode);

  const pageTag = ref({ status: '', options: [] });
  const pageProps = ref<SchemaPageItemProps>({ mainForm: { schemas: [] } });
  const formTabsRef = ref<FormWithTabsExposed | null>(null);

  const globalContext = useSchemaGlobalContext();
  useSchemaRender(props, (schema: PAGE_ITEM_SCHEMA) => {
    try {
      pageProps.value = parsePageItemSchema(schema, { pageMode });
      if (globalContext.schemaModifier) {
        pageProps.value = globalContext.schemaModifier(pageProps.value, {
          pageMode,
        });
      }
      setModals(pageProps.value.modals);

      formTabsRef.value?.setCompProps({ ...pageProps.value, pageMode, record: { id: curId } });

      if (Object.keys(route.query).length) {
        nextTick(() => formTabsRef.value?.setFieldsValue(route.query));
      }
    } catch (error: any) {
      console.error(error);
      message.error(error.message);
    }
  });

  const [registerModal, { modalsRef, setModals, showModal }] = usePageModal();

  const pageRef = computed(() => ({ ...formTabsRef.value }));
  const pageContext = { pageSchema: props.schema, pageRef, pageProps, modalsRef };
  // @ts-ignore
  createSchemaPageContext(pageContext);
  defineExpose(pageContext);

  const actionContext = useSchemaActionContext();
  const { apiActionHandler } = useActionHandler({
    showModal,
    pageContext,
    actionContext,
    actionRef: pageRef,
  });
  const actionHandler: ActionHandlerFn = async (actionEvent, record, index) => {
    if (actionEvent.eventType === 'EXPORT_FILE') {
      return apiActionHandler(
        actionEvent,
        { modelId: actionEvent.modelId, search: { id: curId }, id: curId },
        index,
      );
    }

    try {
      let data = { id: curId, ...record, ...formTabsRef.value?.getPageData() };
      if (pageMode !== 'Detail') {
        data = deepMerge(data, await formTabsRef.value?.validate(), { mergeUndefined: true });
      }
      return apiActionHandler(actionEvent, data, index);
    } catch (e) {
      console.error(e);
    }
  };
</script>
